import { IPeriodDto } from "../generated";
import DatesHelper, { DaysOfWeek } from "./datesHelper";

export class Period implements IPeriodDto {
    from: Date;
    to: Date;

    constructor(periodDto: { from: Date, to: Date }) {
        this.from = periodDto.from;
        this.to = periodDto.to;
    }

    equals(other: { from: Date, to: Date }) {
        return this.from.valueOf() === other.from.valueOf() && this.to.valueOf() === other.to.valueOf();
    }


    getValidDates = function* getValidDates(this: Period, daysOfWeekValidity: DaysOfWeek) {
        const today = DatesHelper.Today();
        if (!DatesHelper.IsDayOfWeek(today, daysOfWeekValidity)) {
            let currentDate = today;
            for (let i = 0; i < 50; ++i) {
                currentDate = DatesHelper.AddDays(currentDate, -1);
                if (currentDate < this.from)
                    break;
                if (DatesHelper.IsDayOfWeek(currentDate, daysOfWeekValidity)) {
                    yield currentDate;
                    break;
                }
            }
        }

        const next2Year = DatesHelper.AddDays(today, 730);
        const to = this.to > next2Year ? next2Year : this.to;
        const allPeriod = Period.Create(today, to);
        for (let date of allPeriod.dates()) {
            if (DatesHelper.IsDayOfWeek(date, daysOfWeekValidity))
                yield date;
        }
    }


    join(period: Period) {
        if (this.containsPeriod(period))
            return this;
        if (period.containsPeriod(this))
            return period;

        if (period.contains(this.from))
            return Period.Create(period.from, this.to);

        if (period.contains(this.to))
            return Period.Create(this.from, period.to);

        if (period.to.valueOf() + 1 == this.from.valueOf())
            return Period.Create(period.from, this.to);

        if (this.to.valueOf() + 1 == period.from.valueOf())
            return Period.Create(this.from, period.to);

        return null;
    }


    contains(date: Date): boolean {
        return date >= this.from && date <= this.to;
    }
    containsPeriod(period: Period): boolean {
        return period.from >= this.from && period.to <= this.to;
    }

    duration() {
        return {
            days: DatesHelper.getDaysOffset(this.from, this.to),
        };
    }

    intersection(period: Period) {
        if (this.containsPeriod(period))
            return period;
        if (period.containsPeriod(this))
            return this;

        if (period.contains(this.from)) {
            let p = new Period({ from: this.from, to: period.to });
            return p;
        }

        if (period.contains(this.to)) {
            let p = new Period({ from: period.from, to: this.to });
            return p
        }
        return undefined;
    }

    fullDays(): Period {
        var result = new Period({ from: DatesHelper.ToDate(this.from), to: DatesHelper.EndOfDay(this.to) });
        return result;
    }

    subtract(period: Period): Period[] {
        let result: Period[] = [];
        if (this.containsPeriod(period)) {
            if (period.from.valueOf() != this.from.valueOf()) {
                let p1 = new Period({
                    from: this.from,
                    to: DatesHelper.AddTicks(period.from, -1),
                });
                result.push(p1);
            }
            if (period.to.valueOf() != this.to.valueOf()) {
                let p2 = new Period({
                    from: DatesHelper.AddTicks(period.to, 1),
                    to: this.to,
                });
                result.push(p2);
            }
            return result;
        }
        if (period.containsPeriod(this))
            return result;

        if (period.contains(this.from)) {
            let p3 = new Period({
                from: DatesHelper.AddTicks(period.to, 1),
                to: this.to,
            });
            result.push(p3)
            return result;
        }

        if (period.contains(this.to)) {
            let p4 = new Period({
                from: this.from,
                to: DatesHelper.AddTicks(period.from, -1),
            });
            result.push(p4)
            return result;
        }

        return [this];
    }

    dates() {
        var result: Date[] = [];
        var current = DatesHelper.ToDate(this.from);
        while (current <= this.to) {
            result.push(current);
            current = DatesHelper.AddDays(current, 1);
        }
        return result;
    }

    toString() {
        return this.from.toString() + " - " + this.to.toString();
    }

    static ForMonth(year: number, month: number) {
        return Period.Create(
            new Date(year, month - 1, 1),
            new Date(year, month, 0)
        ).fullDays();
    }

    static ForFullDays(start: Date, days: number): Period {
        if (days >= 0) {
            const from = DatesHelper.ToDate(start);
            const to = new Date(from.valueOf());
            to.setDate(to.getDate() + days - 1);
            return new Period({ from, to: DatesHelper.EndOfDay(to) });
        }
        else {
            const from = new Date(start.valueOf());
            from.setDate(DatesHelper.ToDate(start).getDate() + days + 1);
            return new Period({ from, to: DatesHelper.EndOfDay(start) });
        }
    }

    static FullDays(start: Date, end: Date): Period {
        const from = DatesHelper.ToDate(start);
        return new Period({ from, to: DatesHelper.EndOfDay(end) });
    }

    static Create(from: Date, to: Date): Period {
        return new Period({ from, to });
    }

    static Invariant(from: Date, to: Date): Period {
        if (from > to) {
            return new Period({ from: to, to: from });
        } else {
            return new Period({ from, to });
        }
    }

    static Today(): Period {
        var res = new Period({
            from: DatesHelper.Today(),
            to: DatesHelper.EndOfDay(DatesHelper.Today()),
        });
        return res;
    }

    static FromToday(): Period {
        return new Period({
            from: DatesHelper.Today(),
            to: this.MaxDate,
        });
    }
    static MinDate = new Date(1901, 0, 1, 0, 0, 0, 0);
    static MaxDate = new Date(2099, 11, 31, 23, 59, 59, 0);
    static Always = Period.Create(Period.MinDate, Period.MaxDate);
}

export default Period;